import { CaretDown, Check } from "@phosphor-icons/react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useMemo, useState } from "react";

type YearSelectProps = {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  options: string[];
  disabled?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
};

export const YearSelect = ({
  value,
  onValueChange,
  placeholder,
  options,
  disabled,
  searchPlaceholder = "Search year",
  emptyText = "No year found.",
  className,
}: YearSelectProps) => {
  const [open, setOpen] = useState(false);

  const selectedValue = useMemo(
    () => options.find((option) => option === value) ?? value ?? "",
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className="truncate">{selectedValue || placeholder}</span>
          <CaretDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList
            className="max-h-60 overflow-y-auto overscroll-contain"
            onWheel={(event) => event.stopPropagation()}
          >
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              <div>
                {options.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => {
                      onValueChange(option);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 size-4 opacity-0", value === option && "opacity-100")} />
                    {option}
                  </CommandItem>
                ))}
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
